import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { InvitacionEnviarInvitacionEmpresaGestionadaAsesoriaCommandProps } from "api/Commands/InvitacionEnviarInvitacionEmpresaGestionadaAsesoriaCommand"
import { ErrorMessage, FieldArray } from "formik"
import { Checkbox } from "primereact/checkbox"
import { InputText } from "primereact/inputtext"
import { Trans, useTranslation } from "react-i18next"
import { ComponentFormInvitacion } from "../../constant"

interface FieldChangeEvent {
  target: {
    value: string
  }
}

const SimpleFormInvitacion = ({
  values,
  errors,
  setFieldValue,
  ...props
}: ComponentFormInvitacion) => {
  const { t } = useTranslation()

  const handleFieldChange = (
    group: string,
    fieldName: string,
    { target: { value } }: FieldChangeEvent,
    index: number
  ) => {
    setFieldValue(
      `${group}.[${index}].[${fieldName}]`,
      value,
      Boolean(
        errors?.[`${group}?.[${index}]?.[${fieldName}]`] || errors?.simple
      )
    )
  }

  return (
    <div
      key={`${props.key}`}
      className='tw-w-full'
    >
      <div className='tw-flex tw-flex-col'>
        <p className='tw-w-full tw-font-base tw-text-normal tw-mb-6 tw-mt-3'>
          {t("empresasGestionadas.modal.simple.text")}
        </p>
        <FieldArray name='simple'>
          {({ push, remove }) => {
            return (
              <>
                <div className='tw-flex tw-flex-col tw-w-full tw-max-h-52 tw-overflow-auto'>
                  {values?.simple?.map(
                    (
                      item: InvitacionEnviarInvitacionEmpresaGestionadaAsesoriaCommandProps,
                      index: number
                    ): JSX.Element => (
                      <div
                        key={`item${index + 1}`}
                        className='tw-mb-6'
                      >
                        <div className='tw-w-full tw-flex tw-justify-between'>
                          <p className='tw-mb-4 tw-font-medium tw-op'>
                            {t("empresasGestionadas.modal.simple.company")}{" "}
                            {index + 1}
                          </p>
                          {values?.simple.length > 1 && (
                            <p
                              className={`${
                                values?.simple.length === 1
                                  ? "tw-opacity-80"
                                  : "tw-cursor-pointer"
                              } tw-text-danger tw-mb-4`}
                              onClick={() => remove(index)}
                            >
                              {t(
                                "empresasGestionadas.modal.simple.deleteEmpresa"
                              )}
                            </p>
                          )}
                        </div>
                        <div className='tw-grid tw-grid-cols-3 tw-gap-4'>
                          <div className='tw-flex tw-flex-col'>
                            <p className='tw-mb-2'>
                              {t("empresasGestionadas.modal.simple.nif")}
                            </p>
                            <InputText
                              value={item?.nif ?? ""}
                              name='nif'
                              className={`${
                                errors?.simple?.[`${index}`]?.nif
                                  ? "!tw-border-danger-20"
                                  : ""
                              } !tw-rounded tw-w-full tw-border-1 tw-h-12`}
                              onChange={(e) => {
                                handleFieldChange("simple", "nif", e, index)
                              }}
                            />
                            <ErrorMessage
                              name={`simple.${index}.nif`}
                              component='div'
                              className='!tw-block !tw-w-full !tw-text-left !tw-text-danger'
                            />
                          </div>
                          <div className='tw-flex tw-flex-col'>
                            <p className='tw-mb-2'>
                              {t(
                                "empresasGestionadas.modal.simple.razonSocial"
                              )}
                            </p>
                            <InputText
                              value={item?.razonSocial ?? ""}
                              name='razonSocial'
                              className={`${
                                errors?.simple?.[`${index}`]?.razonSocial
                                  ? "!tw-border-danger-20"
                                  : ""
                              } !tw-rounded tw-w-full tw-border-1 tw-h-12`}
                              onChange={(e) => {
                                handleFieldChange(
                                  "simple",
                                  "razonSocial",
                                  e,
                                  index
                                )
                              }}
                            />
                            <ErrorMessage
                              name={`simple.${index}.razonSocial`}
                              component='div'
                              className='!tw-block !tw-w-full !tw-text-left !tw-text-danger'
                            />
                          </div>
                          <div className='tw-flex tw-flex-col'>
                            <p className='tw-mb-2'>
                              {t("empresasGestionadas.modal.simple.email")}
                            </p>
                            <InputText
                              value={item?.email ?? ""}
                              name='email'
                              className={`${
                                errors?.simple?.[`${index}`]?.email
                                  ? "!tw-border-danger-20"
                                  : ""
                              } !tw-rounded tw-w-full tw-border-1 tw-h-12`}
                              onChange={(e) => {
                                handleFieldChange("simple", "email", e, index)
                              }}
                              id={`email${index}`}
                              autoComplete='off'
                            />
                            <ErrorMessage
                              name={`simple.${index}.email`}
                              component='div'
                              className='!tw-block !tw-w-full !tw-text-left !tw-text-danger'
                            />
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
                {typeof errors.simple === "string" && (
                  <ErrorMessage
                    name='simple'
                    component='div'
                    className='!tw-block !tw-w-full !tw-text-left !tw-text-danger'
                  />
                )}

                <div
                  onClick={() =>
                    push({
                      email: null,
                      nif: null,
                      razonSocial: null,
                    })
                  }
                  className='tw-w-full tw-h-10 tw-bg-info-10 tw-text-info-100 tw-flex tw-justify-center tw-items-center tw-text-base tw-font-normal tw-cursor-pointer'
                >
                  <div className='tw-w-auto tw-h-auto tw-flex tw-flex-row tw-items-center'>
                    <FontAwesomeIcon
                      className='tw-h-4 tw-w-4 !tw-mr-1'
                      icon={["fal", "plus"]}
                    />
                    {t("empresasGestionadas.modal.simple.addEmpresa")}
                  </div>
                </div>
              </>
            )
          }}
        </FieldArray>
      </div>
      <div className='tw-w-full tw-my-6 tw-flex tw-flex-row'>
        <Checkbox
          onChange={() => {
            setFieldValue(
              "policitySimple",
              !values.policitySimple,
              Boolean(errors?.simple?.policitySimple)
            )
          }}
          inputId='policitySimple'
          className={`${errors.policitySimple ? "p-invalid" : ""}`}
          checked={values.policitySimple}
        />
        <p className='tw-flex-wrap tw-text-wrap tw-ml-3'>
          <Trans
            i18nKey='empresasGestionadas.modal.simple.policity'
            components={[
              <span
                className={`tw-text-info-100 tw-cursor-pointer ${
                  values.showModalPolicitySimple ? "tw-hidden" : ""
                }`}
                key='0'
                onClick={() =>
                  setFieldValue("showModalPolicitySimple", true, false)
                }
              />,
            ]}
          />
          {values.showModalPolicitySimple && (
            <Trans
              i18nKey='empresasGestionadas.modal.simple.policityShowMore'
              components={[
                <a
                  href='https://finanedi.es/proteccion-datos.html'
                  className='tw-text-info-100 tw-cursor-pointer'
                  target='_blank'
                  rel='noopener noreferrer'
                  key='0'
                />,
                <a
                  href='https://finanedi.es/aviso-legal.html'
                  className='tw-text-info-100 tw-cursor-pointer'
                  target='_blank'
                  rel='noopener noreferrer'
                  key='1'
                />,
              ]}
            />
          )}
        </p>
      </div>
    </div>
  )
}

export default SimpleFormInvitacion
